import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import InternalLinks from '@/components/seo/InternalLinks';
import {
  Trophy, TrendingUp, Target, BarChart3, Users, Sparkles,
  ArrowUpDown, Goal
} from 'lucide-react';

const API_BASE = '';

function ContentBlock({ icon: Icon, title, content, color = "emerald" }) {
  if (!content || content.trim().length < 20) return null;
  const colors = {
    emerald: "border-emerald-500/20 bg-emerald-500/5",
    amber: "border-amber-500/20 bg-amber-500/5",
    blue: "border-blue-500/20 bg-blue-500/5",
    purple: "border-violet-500/20 bg-violet-500/5",
    cyan: "border-cyan-500/20 bg-cyan-500/5",
    teal: "border-teal-500/20 bg-teal-500/5",
    orange: "border-orange-500/20 bg-orange-500/5",
  };
  const iconColors = {
    emerald: "text-emerald-400", amber: "text-amber-400", blue: "text-blue-400",
    purple: "text-violet-400", cyan: "text-cyan-400", teal: "text-teal-400", orange: "text-orange-400",
  };
  return (
    <div className={`border rounded-xl p-4 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${iconColors[color]}`} />
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider"
          style={{ fontFamily: 'var(--font-heading)' }}>{title}</h3>
      </div>
      <p className="text-sm text-slate-400 leading-relaxed">{content}</p>
    </div>
  );
}

export default function LeagueSEOContent({ slug }) {
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { currentLang } = useLanguage();

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API_BASE}/api/seo/league/${slug}?lang=${currentLang}`)
      .then(r => r.json())
      .then(data => { setSeoData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, currentLang]);

  if (loading || !seoData) return null;
  if (seoData.fallback) return null;
  const blocks = seoData.content_blocks || {};
  const hasContent = Object.values(blocks).some(v => typeof v === 'string' && v.trim().length >= 50 && !v.includes('Compare odds'));
  if (!hasContent) return null;

  const BLOCKS = [
    { key: 'scoring_trend', icon: Goal, title: t('seo.scoringTrend', 'Scoring Trend'), color: 'orange' },
    { key: 'value_bet_summary', icon: TrendingUp, title: t('seo.valueBets', 'Value Bets'), color: 'emerald' },
    { key: 'top_teams', icon: Users, title: t('seo.topTeams', 'Top Teams'), color: 'blue' },
    { key: 'over_under_trend', icon: ArrowUpDown, title: t('seo.overUnder', 'Over/Under'), color: 'cyan' },
    { key: 'btts_trend', icon: Target, title: t('seo.btts', 'BTTS Trend'), color: 'purple' },
  ];

  const visibleBlocks = BLOCKS.filter(b => blocks[b.key] && blocks[b.key].trim().length >= 20);

  return (
    <div className="space-y-4 mb-6" data-testid="league-seo-content">
      {blocks.league_intro && blocks.league_intro.trim().length >= 20 && (
        <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl p-5" data-testid="league-intro-block">
          <p className="text-sm text-slate-400 leading-relaxed">{blocks.league_intro}</p>
        </div>
      )}

      {visibleBlocks.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleBlocks.map(b => (
            <ContentBlock key={b.key} icon={b.icon} title={b.title} content={blocks[b.key]} color={b.color} />
          ))}
        </div>
      )}

      <InternalLinks links={seoData.internal_links} />
    </div>
  );
}
